import React, { useState, useEffect, useRef } from "react";
import Header from "../components/Header";
import HeroCarousel from "../components/HeroCarousel";
import Footer from "../components/Footer";
import CartDrawer from "../components/CartDrawer";
import ProductGrid from "../components/ProductGrid";
import { QuickViewModal, SimpleModal } from "../components/Modals";
import {
  EasyUpgrades,
  HappyPlace,
  ThinPromoRow,
  AutumnNook,
  BestSellers,
  LargeSplitFeature,
  TwoColumnPromo,
  SectionTitle,
} from "../components/Sections";
import { useToast } from "../hooks/use-toast";
import { products } from "../mock";
import { Lock, Mail } from "lucide-react";

const priceFor = (label) => {
  const map = {
    "Accent Chairs": 379,
    "Coffee Tables": 249,
    "Storage Furniture": 599,
    "Small Space Furniture": 199,
    "Isaiah Collection": 899,
    "Brynn Collection": 1199,
    "Sophie Swivel": 549,
    "Orly Collection": 799,
  };
  return map[label] || 149;
};

// Map header nav labels to product categories
const NAV_TO_CAT = {
  Furniture: "Furniture",
  Rugs: "Rugs",
  Outdoor: "Outdoor",
  Dining: "Dining",
  "Bedding & Bath": "Bedding",
  "Decor & Pillows": "Decor",
  Lighting: "Lighting",
  Kitchen: "Kitchen",
  "Food & Drink": "Kitchen",
  "Fall Shop": "All",
  "New Arrivals": "All",
  Sale: "All",
};

const Home = () => {
  const { toast } = useToast();
  const productsRef = useRef(null);

  const [cartOpen, setCartOpen] = useState(false);
  const [wishOpen, setWishOpen] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const [quickView, setQuickView] = useState(null);
  const [activeCategory, setActiveCategory] = useState("All");

  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("icb_cart") || "[]"); } catch { return []; }
  });
  const [wishlist, setWishlist] = useState(() => {
    try { return JSON.parse(localStorage.getItem("icb_wishlist") || "[]"); } catch { return []; }
  });

  useEffect(() => { localStorage.setItem("icb_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("icb_wishlist", JSON.stringify(wishlist)); }, [wishlist]);

  const scrollToProducts = () => {
    productsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const addToCart = (item, qty = 1) => {
    const priceValue = item.price ?? priceFor(item.label || item.name);
    const label = item.name || item.label;
    const id = item.id;
    setCart((prev) => {
      const existing = prev.find((c) => c.id === id);
      if (existing) return prev.map((c) => (c.id === id ? { ...c, qty: c.qty + qty } : c));
      return [...prev, { id, label, image: item.image, qty, priceValue }];
    });
    toast({
      title: "Added to bag",
      description: `${label} \u2014 $${priceValue.toFixed(2)}${qty > 1 ? ` (x${qty})` : ""}`,
    });
  };

  const toggleWish = (item) => {
    setWishlist((prev) => {
      if (prev.includes(item.id)) {
        toast({ title: "Removed from wishlist", description: item.name || item.label });
        return prev.filter((i) => i !== item.id);
      }
      toast({ title: "Saved to wishlist", description: item.name || item.label });
      return [...prev, item.id];
    });
  };

  const updateQty = (id, qty) => {
    if (qty <= 0) return removeItem(id);
    setCart(cart.map((c) => (c.id === id ? { ...c, qty } : c)));
  };
  const removeItem = (id) => setCart(cart.filter((c) => c.id !== id));

  const cartCount = cart.reduce((s, i) => s + i.qty, 0);

  const handleNavClick = (label) => {
    const cat = NAV_TO_CAT[label] || "All";
    setActiveCategory(cat);
    scrollToProducts();
    toast({ title: label, description: cat === "All" ? "Showing all products" : `Filtering by ${cat}` });
  };

  const wishlistItems = products.filter((p) => wishlist.includes(p.id));

  const handleFooterLink = (label) => {
    // Route certain links to real actions
    const routes = {
      "Contact Us": () => toast({ title: "Contact Us", description: "Call 1 (800) 979-0730 — Mon–Sat, 9AM–9PM EST." }),
      "Order Status": () => setSignInOpen(true),
      "New Arrivals": () => { setActiveCategory("All"); scrollToProducts(); },
      "Best Sellers": () => { setActiveCategory("All"); scrollToProducts(); },
      "Sale": () => { setActiveCategory("All"); scrollToProducts(); },
      "Rewards Program": () => toast({ title: "Rewards Program", description: "Sign up to earn points on every purchase." }),
      "Trade Program": () => toast({ title: "Trade Program", description: "Designers save up to 20% on qualifying orders." }),
      "Interior Design Services": () => toast({ title: "Design Services", description: "Free consultations available in-store or online." }),
      "Gift Cards": () => toast({ title: "Gift Cards", description: "Digital and physical gift cards from $25\u2013$500." }),
      "Registry": () => toast({ title: "Wedding & Home Registry", description: "Create your dream registry in minutes." }),
      "Corporate Gifts": () => toast({ title: "Corporate Gifts", description: "Contact our team for bulk & custom orders." }),
      "Affiliate Program": () => toast({ title: "Affiliate Program", description: "Earn commission on every referral sale." }),
      "Careers": () => toast({ title: "Careers", description: "We're hiring! Explore open positions." }),
      "About Us": () => toast({ title: "About Inter Coastal Builders", description: "Handcrafted furniture & decor since 1958." }),
      "Sustainability": () => toast({ title: "Our Commitment", description: "Ethically sourced materials, fair-trade partnerships." }),
      "Investor Relations": () => toast({ title: "Investor Relations", description: "Financial reports & shareholder resources." }),
      "Shipping Info": () => toast({ title: "Shipping", description: "Free standard shipping on orders over $99." }),
      "Returns & Exchanges": () => toast({ title: "Easy Returns", description: "90-day returns on most items \u2014 no questions asked." }),
      "Product Recalls": () => toast({ title: "Product Recalls", description: "No active recalls at this time." }),
      "FAQ": () => toast({ title: "FAQ", description: "Find answers to common questions in our Help Center." }),
      "Terms & Conditions": () => toast({ title: "Terms & Conditions", description: "Last updated August 2025." }),
      "Privacy Notice": () => toast({ title: "Privacy Notice", description: "We respect your data. Read our full policy." }),
      "Accessibility": () => toast({ title: "Accessibility", description: "Committed to WCAG 2.1 AA compliance." }),
      "Do Not Sell My Info": () => toast({ title: "Privacy Choices", description: "Your preferences have been saved." }),
    };
    (routes[label] || (() => toast({ title: label, description: "Coming soon." })))();
  };

  return (
    <div className="min-h-screen bg-white font-body">
      <Header
        cartCount={cartCount}
        wishCount={wishlist.length}
        onCartOpen={() => setCartOpen(true)}
        onWishOpen={() => setWishOpen(true)}
        onSignIn={() => setSignInOpen(true)}
        onNavClick={handleNavClick}
      />

      <HeroCarousel onCtaClick={scrollToProducts} />

      <EasyUpgrades onAdd={addToCart} />

      <ProductGrid
        ref={productsRef}
        activeCategory={activeCategory}
        setActiveCategory={setActiveCategory}
        onAdd={addToCart}
        onQuickView={setQuickView}
        onWish={toggleWish}
        wishlist={wishlist}
      />

      <HappyPlace onSelectCategory={(c) => { setActiveCategory(c); scrollToProducts(); }} />

      <ThinPromoRow
        title="First Look: The Fall Shop"
        sub="Explore the season, from cozy layers to rich hues."
        cta="Explore Fall"
        bg="linear-gradient(90deg,#8a3a1f, #b8542a)"
        onClick={() => { setActiveCategory("All"); scrollToProducts(); }}
      />

      <AutumnNook onSelectCategory={(c) => { setActiveCategory(c); scrollToProducts(); }} />

      <ThinPromoRow
        title="Earn up to $15 in Rewards"
        sub="With every food & drink purchase this month."
        cta="Play Now"
        bg="#1f4d3f"
        onClick={() => toast({ title: "Rewards", description: "Sign up to start earning rewards today." })}
      />

      <section className="px-4 md:px-10 py-14 bg-white">
        <SectionTitle subtitle="Salty, sweet, or spicy — your HQ for every flavor fix.">
          Destination: Cravings!
        </SectionTitle>
        <div className="max-w-6xl mx-auto zoom-wrap aspect-[16/7] rounded-sm">
          <img
            src="https://images.pexels.com/photos/8988956/pexels-photo-8988956.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Cravings"
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        <div className="text-center mt-6">
          <button
            onClick={() => { setActiveCategory("Kitchen"); scrollToProducts(); }}
            className="border border-[color:var(--brand-ink)] text-[color:var(--brand-ink)] px-8 py-3 text-xs font-semibold uppercase tracking-wider hover:bg-[color:var(--brand-ink)] hover:text-white transition-colors"
          >
            Shop the Pantry
          </button>
        </div>
      </section>

      <LargeSplitFeature
        eyebrow="Looks to Dine In On"
        title="Wallet-friendly wows for every fall feast."
        body="Bring the family together around thoughtfully crafted tables, chairs, and serveware built to last through many seasons of memories."
        cta="Shop Dining Furniture"
        image="https://images.unsplash.com/photo-1604578762246-41134e37f9cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxkaW5pbmclMjB0YWJsZXxlbnwwfHx8fDE3ODQwNzAxNDB8MA&ixlib=rb-4.1.0&q=85&w=1200"
        onClick={() => { setActiveCategory("Dining"); scrollToProducts(); }}
      />

      <BestSellers onAdd={addToCart} />

      <LargeSplitFeature
        reverse
        eyebrow="Entertaining Made Easy"
        title="Cafe-Inspired Charm"
        body="Bring Parisian panache to brunches and beyond with bistro chairs, marble serveware, and warm brass accents."
        cta="Shop Entertaining"
        image="https://images.unsplash.com/photo-1617806265182-7b3f847f0b75?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHw0fHxib2hvJTIwZGVjb3J8ZW58MHx8fHwxNzg0MDcwMTMyfDA&ixlib=rb-4.1.0&q=85&w=1200"
        onClick={() => { setActiveCategory("Dining"); scrollToProducts(); }}
      />

      <TwoColumnPromo
        onClick={(it) => toast({ title: it.title, description: `${it.cta} \u2014 details coming soon.` })}
        items={[
          {
            badge: "Premier Credit Card",
            title: "Save 30% When Approved Online",
            body: "Earn 10% back on every purchase, plus exclusive member perks and extra rewards.",
            cta: "Learn More",
            image:
              "https://images.unsplash.com/photo-1616047006789-b7af5afb8c20?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHw0fHxob21lJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg0MDcwMTMxfDA&ixlib=rb-4.1.0&q=85&w=1200",
          },
          {
            badge: "Explorer Membership",
            title: "Get 15% Off With Signup",
            body: "Enjoy exclusive member perks, insider deals, and so much more with our free rewards program.",
            cta: "Join Now",
            image:
              "https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwzfHxob21lJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg0MDcwMTMxfDA&ixlib=rb-4.1.0&q=85&w=1200",
          },
        ]}
      />

      <section className="px-4 md:px-10 py-14 bg-[color:var(--brand-cream)]">
        <SectionTitle subtitle="Tag @intercoastalbuilders on Instagram for a chance to be featured.">
          Share Your #InterCoastalFinds
        </SectionTitle>
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
          {[
            "https://images.unsplash.com/photo-1631679706909-1844bbd07221?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxsaXZpbmclMjByb29tfGVufDB8fHx8MTc4NDA3MDEzMXww&ixlib=rb-4.1.0&q=85&w=500",
            "https://images.unsplash.com/photo-1616046229478-9901c5536a45?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxib2hvJTIwZGVjb3J8ZW58MHx8fHwxNzg0MDcwMTMyfDA&ixlib=rb-4.1.0&q=85&w=500",
            "https://images.unsplash.com/photo-1613685302957-3a6fc45346ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxvdXRkb29yJTIwcGF0aW98ZW58MHx8fHwxNzg0MDcwMTQwfDA&ixlib=rb-4.1.0&q=85&w=500",
            "https://images.unsplash.com/photo-1558603668-6570496b66f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxib2hvJTIwZGVjb3J8ZW58MHx8fHwxNzg0MDcwMTMyfDA&ixlib=rb-4.1.0&q=85&w=500",
          ].map((src, i) => (
            <button
              key={i}
              onClick={() => window.open("https://instagram.com", "_blank")}
              className="zoom-wrap aspect-square block"
            >
              <img src={src} alt={`Community find ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      </section>

      <Footer onLinkClick={handleFooterLink} />

      <CartDrawer
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        items={cart}
        updateQty={updateQty}
        removeItem={removeItem}
      />

      <QuickViewModal
        product={quickView}
        onClose={() => setQuickView(null)}
        onAdd={addToCart}
        onWish={toggleWish}
        wished={quickView ? wishlist.includes(quickView.id) : false}
      />

      {/* Wishlist Modal */}
      <SimpleModal open={wishOpen} onClose={() => setWishOpen(false)} title={`Your Wishlist (${wishlist.length})`}>
        {wishlistItems.length === 0 ? (
          <p className="text-sm text-neutral-600">Your wishlist is empty. Tap the heart icon on any product to save it here.</p>
        ) : (
          <ul className="max-h-80 overflow-y-auto space-y-3">
            {wishlistItems.map((p) => (
              <li key={p.id} className="flex gap-3 items-center border-b pb-3">
                <img src={p.image} alt={p.name} className="w-14 h-14 object-cover rounded-sm" />
                <div className="flex-1">
                  <div className="text-sm font-medium">{p.name}</div>
                  <div className="text-xs text-neutral-500">${p.price.toFixed(2)}</div>
                </div>
                <button onClick={() => addToCart(p)} className="text-xs font-semibold uppercase tracking-widest border-b border-[color:var(--brand-ink)] hover:text-[color:var(--brand-red)] hover:border-[color:var(--brand-red)]">
                  Add to Bag
                </button>
              </li>
            ))}
          </ul>
        )}
      </SimpleModal>

      {/* Sign In Modal */}
      <SimpleModal open={signInOpen} onClose={() => setSignInOpen(false)} title="Sign In">
        <p className="text-sm text-neutral-600 mb-5">Access your rewards, orders, and saved items.</p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setSignInOpen(false);
            toast({ title: "Welcome back!", description: "You are now signed in (demo)." });
          }}
          className="space-y-4"
        >
          <div className="relative">
            <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input required type="email" placeholder="Email address" className="w-full pl-10 pr-4 py-3 border border-neutral-300 text-sm outline-none focus:border-[color:var(--brand-red)]" />
          </div>
          <div className="relative">
            <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input required type="password" placeholder="Password" className="w-full pl-10 pr-4 py-3 border border-neutral-300 text-sm outline-none focus:border-[color:var(--brand-red)]" />
          </div>
          <button className="w-full bg-[color:var(--brand-red)] hover:bg-[color:var(--brand-red-dark)] text-white py-3 text-xs font-semibold uppercase tracking-widest transition-colors">
            Sign In
          </button>
          <div className="text-center text-xs text-neutral-500">
            New here? <button type="button" className="text-[color:var(--brand-red)] font-medium hover:underline">Create an account</button>
          </div>
        </form>
      </SimpleModal>

    </div>
  );
};

export default Home;
